/**
 * Measurement channel that bypasses React.
 *
 * Pitch frames arrive ~22 times a second and level updates ~30, and the bars
 * are redrawn at display rate. Routing any of that through React state would
 * re-render the whole tree dozens of times a second for values that only ever
 * move a needle. Instead the engine writes here, and the render loop reads
 * from here and writes straight to the DOM.
 *
 * React still owns everything structural: which strings exist, the tuning, the
 * transport state. Only the fast-moving numbers live in this module.
 */

import type { LevelReading } from '../audio/engine'
import type { StringReading } from '../audio/string-tracker'
import { MedianTracker } from '../music/median-tracker'

/** The most recent measurement for one string. */
export interface LiveString {
  /** Measured fundamental in Hz, or null when the string is quiet. */
  frequency: number | null
  /** Timestamp of the last measurement, or null if there has been none. */
  updatedAt: number | null
}

/** Snapshot handed to the render loop. */
export interface LiveSnapshot {
  readonly strings: readonly LiveString[]
  readonly level: LevelReading
}

/**
 * Mutable store of the latest measurements.
 *
 * Entries are mutated in place rather than reallocated: this is written to on
 * every audio frame and read on every animation frame, so it stays allocation
 * free in the steady state.
 */
export class LiveReadings {
  private strings: LiveString[] = []
  private trackers: MedianTracker[] = []
  private level: LevelReading = { rms: 0, peak: 0 }
  private muted = false

  /** Resize and reset for a new set of strings. */
  reset(count: number): void {
    this.strings = Array.from({ length: count }, () => ({
      frequency: null,
      updatedAt: null,
    }))
    this.trackers = Array.from({ length: count }, () => new MedianTracker())
    this.level = { rms: 0, peak: 0 }
  }

  /** Number of strings currently tracked. */
  get size(): number {
    return this.strings.length
  }

  /** True while incoming measurements are being discarded. */
  get isMuted(): boolean {
    return this.muted
  }

  /**
   * Stop accepting measurements, or start again.
   *
   * The tuner and the reference tone share one room: the moment a tone plays,
   * the microphone hears it, and the meters would read the app's own output
   * instead of the instrument. Worse than useless — the display would confirm
   * the string is perfectly in tune while the player has not touched it.
   *
   * Muting the channel rather than a single bar is deliberate. The reference
   * tone is a plucked string, harmonics and all, and those harmonics land on
   * the other strings of the tuning: an E2 lights up E3, B3 and E4 as well.
   *
   * Muting drops the readings collected so far, so nothing stale is left to
   * reappear when the channel opens again.
   */
  setMuted(muted: boolean): void {
    if (this.muted === muted) return
    this.muted = muted
    if (muted) this.clear()
  }

  /**
   * Record what the string tracker measured.
   *
   * Does nothing while the channel is muted; see {@link setMuted}.
   *
   * A reading the tracker marked as a ghost is treated as silence: the energy
   * at that frequency is a lower string's overtone, and a meter that follows
   * it is answering a question nobody asked.
   *
   * @param now - Timestamp to age the readings against, in milliseconds.
   */
  applyReadings(readings: readonly StringReading[], now: number): void {
    if (this.muted) return
    if (this.strings.length !== readings.length) {
      this.reset(readings.length)
    }

    readings.forEach((reading, index) => {
      const slot = this.strings[index]
      const tracker = this.trackers[index]
      if (!slot || !tracker) return

      if (reading.frequency === null || reading.isGhost) {
        tracker.pushSilence()
      } else {
        tracker.push(reading.frequency)
      }

      // Only a frequency the median tracker will vouch for reaches the UI: a
      // single block is not enough, and one wild block cannot move it.
      const settled = tracker.value()
      if (settled !== null) {
        slot.frequency = settled
        slot.updatedAt = now
      }
    })
  }

  /**
   * Record a level reading.
   *
   * Not gated by {@link setMuted}: the level meter answers "is the input
   * alive", and that question stays worth answering while a reference tone
   * sounds. Only the pitch readings would be wrong.
   */
  applyLevel(level: LevelReading): void {
    this.level = level
  }

  /** Clear every measurement, keeping the string count. */
  clear(): void {
    for (const slot of this.strings) {
      slot.frequency = null
      slot.updatedAt = null
    }
    for (const tracker of this.trackers) tracker.clear()
    this.level = { rms: 0, peak: 0 }
  }

  /** Live view of the current data. Not a copy — read it, do not keep it. */
  snapshot(): LiveSnapshot {
    return { strings: this.strings, level: this.level }
  }
}

/** Shared instance: one microphone, one set of readings. */
export const liveReadings = new LiveReadings()
